// src/components/captures/CaptureCard.tsx
import React, { useState, useMemo } from 'react';
import { Capture } from '../../types/capture';
import { updateCapture } from '../../services/api/captures';
import CaptureModal from './CaptureModal';
import { Archive, ArchiveRestore, CheckSquare } from '../common/icons';
import CaptureViewModal from './CaptureViewModal';
import CaptureToTaskModal from './CaptureToTaskModal';
import { formatTime12Hour, toDateObject } from '../../utils/dateUtils';
import { eventBus, EVENTS } from '../../utils/eventBus';
import { logError } from '../../utils/logger';
import { useMediaQuery } from '../../hooks/useMediaQuery';

interface CaptureCardProps {
  capture: Capture;
  onUpdate?: () => void;
  onDelete?: (captureId: string) => void;
  // Lesson 模式
  isLessonMode?: boolean;
  isSeedCapture?: boolean; // 是否為 Lesson 1 的示範 Capture（高亮 To Task 按鈕）
}

const CaptureCard: React.FC<CaptureCardProps> = ({ capture, onUpdate, onDelete, isLessonMode = false, isSeedCapture = false }) => {
  const isMobile = useMediaQuery('(max-width: 639px)');

  // Modal 狀態管理
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCaptureToTaskModal, setShowCaptureToTaskModal] = useState(false);
  const [wasViewModalOpen, setWasViewModalOpen] = useState(false); // 追蹤 ViewModal 是否曾開啟

  // 內容截斷設定，限制為 120 字符
  const MAX_CONTENT_LENGTH = 120;
  const isContentTruncated = capture.content.length > MAX_CONTENT_LENGTH;
  const displayContent = isContentTruncated
    ? `${capture.content.substring(0, MAX_CONTENT_LENGTH)}...`
    : capture.content;

  // 處理完成/取消完成 (封存/取消封存)
  const handleMarkAsDone = async () => {
    try {
      await updateCapture(capture.id, { processed: !capture.processed });
      if (onUpdate) onUpdate();
    } catch (error) {
      logError('Error updating capture status:', error);
    }
  };

  // 處理卡片點擊（進入編輯模式）
  const handleCardClick = () => {
    setShowViewModal(true);
    // setShowEditModal(true);
  };

  // 添加新的處理程序
  const handleToggleArchive = async () => {
    try {
      await updateCapture(capture.id, { processed: !capture.processed });
      if (onUpdate) onUpdate();
      // 可選：關閉 modal
      setShowViewModal(false);
    } catch (error) {
      logError('Error updating capture status:', error);
    }
  };

  // 更新刪除處理程序
  const handleDelete = () => {
    try {
      if (onDelete) {
        onDelete(capture.id);
      }
      // 關閉所有 modal
      setShowViewModal(false);
    } catch (error) {
      logError('Error deleting capture:', error);
    }
  };

  // 處理捕獲編輯提交
  const handleEditSubmit = async (content: string, relatedToCurrentTimeBox: boolean) => {
    try {
      await updateCapture(capture.id, { content });
      setShowEditModal(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      logError('Error updating capture:', error);
      throw error; // 讓 CaptureModal 可以捕獲錯誤
    }
  };

  // 處理轉換為任務 - 新的 AI 分析流程
  const handleConvert = () => {
    // 通知 Lesson Guide：To Task Modal 即將開啟（讓 Tooltip 立即關閉）
    eventBus.publish(EVENTS.LESSON.CAPTURE_TO_TASK_OPENED);
    setWasViewModalOpen(showViewModal);  // 記錄 ViewModal 是否開啟
    setShowViewModal(false);  // 關閉 CaptureViewModal (完全替換模式)
    setShowCaptureToTaskModal(true);
  };

  // 任務建立完成後的處理
  const handleTasksCreated = async () => {
    // 發出 Lesson 事件，讓 Lesson1Guide 知道用戶完成了 Capture → Task
    eventBus.publish(EVENTS.LESSON.CAPTURE_TO_TASK_COMPLETED);

    try {
      // 自動封存 capture
      await updateCapture(capture.id, { processed: true });

      // 短暫延遲讓用戶看到成功訊息,然後關閉 modal
      setTimeout(() => {
        // 成功時: 關閉 CaptureToTaskModal,不重新開啟 ViewModal
        setShowCaptureToTaskModal(false);
        setWasViewModalOpen(false);  // 重置狀態
      }, 1000);

      // 更新父組件以刷新 Capture 列表
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      logError('封存 Capture 失敗:', error);
      // 即使封存失敗，任務已創建成功，所以只記錄錯誤

      // 仍然延遲後關閉 modal 並重置狀態
      setTimeout(() => {
        setShowCaptureToTaskModal(false);
        setWasViewModalOpen(false);
      }, 1000);

      // 仍然更新父組件以顯示任務已創建
      if (onUpdate) {
        onUpdate();
      }
    }
  };


  // 根據內容長度和類型應用不同的樣式
  const getContentStyle = () => {
    // 如果內容含有特定格式（如清單項、代碼等），保留特殊樣式
    if (capture.content.includes('- ') || capture.content.includes('* ')) {
      return 'text-gray-700';
    }

    // 對所有內容使用固定大小
    return 'text-base text-gray-800';
  };

  // 截取標題（如果內容第一行可以當作標題）
  const getTitle = () => {
    const firstLine = capture.content.split('\n')[0];
    if (firstLine && firstLine.length < 50) {
      return firstLine;
    }
    return null;
  };

  const title = getTitle();

  return (
    <div className="bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors cursor-pointer">
      {/* 點擊卡片內容區域打開編輯模態框 */}
      <div className="p-4 cursor-pointer flex flex-col" onClick={handleCardClick}>
        {/* 時間顯示 - 使用 useMemo 快取轉換結果 */}
        <div className="text-sm text-gray-500 mb-3">
          {useMemo(() => {
            return capture.createdAt ? formatTime12Hour(toDateObject(capture.createdAt)) : 'Unknown time';
          }, [capture.createdAt])}
        </div>
        
        {title && title !== capture.content && (
          <div className="font-medium text-lg mb-1">{title}</div>
        )}

        <div className={getContentStyle()}>
          {displayContent}
        </div>

        {/* 操作按鈕區 - 響應式設計 */}
        <div className="flex items-center gap-1.5 sm:gap-1 mt-4 pt-3 border-t border-gray-100">
          {/* 轉換為任務按鈕 */}
          <div className={isSeedCapture ? 'relative flex-1 sm:flex-none' : 'flex-1 sm:flex-none'}>
            {/* 行動版 Seed Capture：右上角通知點 + ping */}
            {isSeedCapture && isMobile && (
              <span className="absolute -top-2 -right-2 flex h-4 w-4 pointer-events-none z-10">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-300 opacity-60" />
                <span className="relative inline-flex rounded-full h-4 w-4 bg-sky-400" />
              </span>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleConvert();
              }}
              {...(isSeedCapture ? { 'data-lesson-anchor': 'seed-to-task' } : {})}
              className={[
                'w-full inline-flex items-center justify-center gap-1.5 sm:gap-1 px-3 py-2 sm:px-2.5 sm:py-1.5 rounded-md text-xs sm:text-xs font-medium transition-all duration-150 touch-manipulation',
                'bg-sky-50 hover:bg-sky-100 border border-sky-200 hover:border-sky-300 text-sky-600 hover:text-sky-700',
              ].join(' ')}
              title="Convert to Task"
            >
              <CheckSquare size={14} className="sm:w-3 sm:h-3" />
              <span>To Task</span>
            </button>
          </div>

          {/* 封存/取消封存按鈕 */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleMarkAsDone();
            }}
            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 sm:gap-1 px-3 py-2 sm:px-2.5 sm:py-1.5 bg-gray-50 hover:bg-gray-100 border border-gray-200 hover:border-gray-300 rounded-md text-xs sm:text-xs font-medium text-gray-600 hover:text-gray-700 transition-all duration-150 touch-manipulation"
            title={capture.processed ? "Restore" : "Archive"}
          >
            {capture.processed ? <ArchiveRestore size={14} className="sm:w-3 sm:h-3" /> : <Archive size={14} className="sm:w-3 sm:h-3" />}
            <span>
              {capture.processed ? "Restore" : "Archive"}
            </span>
          </button>
        </div>
      </div>

      {/* 編輯模態框 */}
      {showEditModal && (
        <CaptureModal
          isOpen={true}
          onClose={() => {
            setShowEditModal(false); // 關閉編輯模態框
            setShowViewModal(true);  // 重新顯示查看模態框
          }}
          onSubmit={async (content, relatedToCurrentTimeBox) => {
            try {
              await handleEditSubmit(content, relatedToCurrentTimeBox);
              setShowEditModal(false); // 編輯成功後關閉編輯模態框
              setShowViewModal(true);  // 重新顯示查看模態框，可以看到更新後的內容
            } catch (error) {
              // 錯誤處理...
            }
          }}
          initialContent={capture.content}
          userId={capture.userId}
          isEditing={true}
          title="Edit Capture"
        />
      )}

      {/* CaptureViewModal */}
      {showViewModal && (
        <CaptureViewModal
          isOpen={showViewModal}
          onClose={() => setShowViewModal(false)}
          capture={capture}
          onEdit={() => {
            setShowEditModal(true);
            setShowViewModal(false); // 關閉查看模態框
          }}
          onDelete={handleDelete}
          onToggleArchive={handleToggleArchive}
          onConvertToTask={handleConvert}
        />
      )}

      {/* CaptureToTaskModal for AI-powered conversion */}
      {showCaptureToTaskModal && (
        <CaptureToTaskModal
          isOpen={showCaptureToTaskModal}
          onClose={() => {
            setShowCaptureToTaskModal(false);
            // 取消時: 只有之前開啟過 ViewModal 才重新開啟
            if (wasViewModalOpen) {
              setShowViewModal(true);
            }
            setWasViewModalOpen(false);  // 重置狀態
          }}
          capture={capture}
          onTasksCreated={handleTasksCreated}
          showLessonTip={isLessonMode}
        />
      )}
    </div>
  );
};

export default CaptureCard;